import { SearchURL, MapsURL, SubscriptionKeyCredential, Aborter, SearchAddressResponse } from 'azure-maps-rest';
import { ChannelConfiguration } from '@msdyn365-commerce/retail-proxy';
import { ISuggestion } from '../components/new-checkout/address-input';
import * as Msdyn365 from '@msdyn365-commerce/core';

// Create a configured Azure Maps instance
export const createAzureMapsInstance = (
    channel: ChannelConfiguration,
    locale: string = 'en',
    telemetry: Msdyn365.ITelemetry | undefined = undefined
) => {
    const pipeline = MapsURL.newPipeline(new SubscriptionKeyCredential(channel?.AzureMapsApiKey || ''));
    const searchClient: SearchURL = new SearchURL(pipeline);
    const countryRegionISOCode = channel.ChannelCountryRegionISOCode || '';

    let debounceTimer: NodeJS.Timeout | null = null;
    let previousResolve: ((value: ISuggestion[]) => void) | null = null;
    let previousReject: ((reason?: any) => void) | null = null;

    const getSuggestions = async (query: string, debounceDelay: number = 300): Promise<ISuggestion[]> => {
        // Skip API calls for short queries
        if (query.length < 3) {
            return [];
        }

        return new Promise((resolve, reject) => {
            // Resolve the previous promise to prevent it from hanging
            if (previousResolve) {
                previousResolve([]);
            }

            // Store the current resolve and reject for potential cancellation
            previousResolve = resolve;
            previousReject = reject;

            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(() => {
                const searchTimeout = 10000;
                const entityTypeMunicipality = 'Municipality';

                searchClient
                    .searchFuzzy(Aborter.timeout(searchTimeout), query, {
                        limit: 5,
                        maxFuzzyLevel: 1,
                        language: locale,
                        typeahead: true,
                        ...(countryRegionISOCode ? { countrySet: [countryRegionISOCode] } : {}),
                        entityType: [entityTypeMunicipality]
                    })
                    .then(response => {
                        // Only resolve if this is still the current request
                        if (previousResolve === resolve) {
                            previousResolve(
                                response.results?.map(result => ({
                                    formattedSuggestion: result.address?.freeformAddress || '',
                                    title: result.address?.freeformAddress || '',
                                    subtitle: result.address?.municipality || '',
                                    address: result.address ?? {}
                                })) || []
                            );
                            previousResolve = null;
                        }
                    })
                    .catch(error => {
                        if (telemetry) {
                            telemetry.error('Error fetching suggestions from Azure Maps:', error);
                        }
                        // Only reject if this is still the current request
                        if (previousReject === reject) {
                            previousReject([]);
                            previousReject = null;
                        }
                    });
            }, debounceDelay);
        });
    };

    const searchAddress = async (query: string): Promise<SearchAddressResponse> => {
        return new Promise((resolve, reject) => {
            const searchTimeout = 10000;

            searchClient
                .searchAddress(Aborter.timeout(searchTimeout), query, {
                    limit: 1,
                    language: locale,
                    ...(channel.ChannelCountryRegionISOCode
                        ? {
                              countrySet: [channel.ChannelCountryRegionISOCode]
                          }
                        : {})
                })
                .then(resolve)
                .catch(error => {
                    if (telemetry) {
                        telemetry.error('Error searching address with Azure Maps:', error);
                    }
                    reject([]);
                });
        });
    };
    return {
        searchClient,
        locale,
        countryRegionISOCode,
        getSuggestions,
        searchAddress
    };
};
